import React from "react";

// Renders a form straight from an Airbyte connector's real JSON-schema
// config spec (POST /source_definition_specifications/get — see
// airbyte-api.ts), instead of a hardcoded per-connector field list. Handles
// what Facebook Marketing's real schema actually uses: flat string/number/
// boolean fields, arrays of strings or enums, and one level of `oneOf`
// (its "credentials" field — OAuth vs long-lived access token). Doesn't
// attempt full JSON-schema generality (nested oneOf-of-oneOf, $ref, allOf,
// array-of-object items) — those render as a skipped "advanced option" note
// instead of a broken field, since none of Facebook Marketing's *required*
// fields need them.

export interface JsonSchemaProperty {
  type?: string;
  title?: string;
  description?: string;
  default?: any;
  examples?: any[];
  order?: number;
  const?: any;
  enum?: any[];
  format?: string;
  items?: { type?: string; enum?: any[] };
  oneOf?: Array<{ title?: string; required?: string[]; properties: Record<string, JsonSchemaProperty> }>;
  airbyte_hidden?: boolean;
  airbyte_secret?: boolean;
  required?: string[];
  properties?: Record<string, JsonSchemaProperty>;
}

export interface JsonSchema {
  type: string;
  required?: string[];
  properties: Record<string, JsonSchemaProperty>;
}

const inputCls =
  "w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20";

const humanize = (key: string) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Airbyte's date-time fields want a literal "YYYY-MM-DDTHH:mm:ssZ" string
// (confirmed live via Facebook Marketing's own start_date/end_date pattern)
// — a native datetime-local input works in "YYYY-MM-DDTHH:mm" terms with no
// timezone of its own, so this only reformats the string. It deliberately
// never routes through a JS Date, which would silently shift the value by
// the browser's own timezone offset instead of leaving it exactly as typed.
function isoToDatetimeLocal(iso: any): string {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(typeof iso === "string" ? iso : "");
  return m ? `${m[1]}T${m[2]}` : "";
}
function datetimeLocalToIso(local: string): string {
  return local ? `${local}:00Z` : "";
}

const isSkippable = (prop: JsonSchemaProperty) => prop.airbyte_hidden || (prop.type === "array" && prop.items?.type === "object");

// A oneOf variant's own "which variant is this" field (e.g. `auth_type`,
// fixed via `const`) is never user-editable — it's set automatically from
// the chosen variant's own const value when the config is assembled.
const isConstField = (prop: JsonSchemaProperty) => prop.const !== undefined;

const sortedEntries = (properties: Record<string, JsonSchemaProperty>) =>
  Object.entries(properties)
    .filter(([, prop]) => !isSkippable(prop) && !isConstField(prop))
    .sort(([, a], [, b]) => (a.order ?? Infinity) - (b.order ?? Infinity));

// Seeds a value object with sensible defaults for every field in a schema —
// used both to initialize the whole form and to reset a oneOf field when the
// operator switches which variant they're authenticating with.
export function defaultsForSchema(schema: JsonSchema | { properties: Record<string, JsonSchemaProperty> }): Record<string, any> {
  const values: Record<string, any> = {};
  Object.entries(schema.properties).forEach(([key, prop]) => {
    if (isSkippable(prop)) return;
    values[key] = defaultForProp(prop);
  });
  return values;
}

// For a oneOf field (e.g. Facebook Marketing's "credentials": OAuth app vs.
// a plain access token), default to whichever variant asks for the *fewest*
// required fields rather than always the first one. Confirmed against
// Airbyte's own docs for Facebook Marketing specifically: the recommended,
// typical setup is the access-token-only variant, not the Client ID/Client
// Secret OAuth-app variant — which happens to also be the one with fewer
// required fields, so this generic rule gives the right default without
// hardcoding Facebook-specific knowledge into a schema-generic form.
function simplestVariantIndex(oneOf: Array<{ required?: string[]; properties: Record<string, JsonSchemaProperty> }>): number {
  let bestIndex = 0;
  let bestCount = Infinity;
  oneOf.forEach((variant, i) => {
    const count = (variant.required || []).length;
    if (count < bestCount) {
      bestCount = count;
      bestIndex = i;
    }
  });
  return bestIndex;
}

function defaultForProp(prop: JsonSchemaProperty): any {
  if (prop.const !== undefined) return prop.const;
  if (prop.oneOf?.length) {
    const variant = prop.oneOf[simplestVariantIndex(prop.oneOf)];
    return { ...defaultsForSchema({ properties: variant.properties }) };
  }
  if (prop.type === "array") return prop.default ?? [];
  if (prop.type === "boolean") return prop.default ?? false;
  if (prop.type === "integer" || prop.type === "number") return prop.default ?? "";
  // A required enum field with no explicit `default` (e.g. RabbitMQ Bounded's
  // single-valued `acknowledgement_mode`) left this at "" — the <select>'s
  // only `<option>` still rendered selected in the DOM (no empty option to
  // fall back to once the field is required), so it looked filled in while
  // the real value was still "", failing the required check on submit.
  if (prop.enum?.length) return prop.default ?? prop.enum[0];
  return prop.default ?? "";
}

// Confirmed live against Facebook Marketing's real spec: `account_ids` is a
// required array field with `minItems: 1` — nothing in this form stopped a
// user from submitting it empty, so the request reached Airbyte and came
// back as a raw "$.account_ids: must have at least 1 items but found 0"
// JSON-schema error instead of a clear inline message. validateSchema below
// catches that client-side before the request ever goes out.
function isEmptyValue(prop: JsonSchemaProperty, value: any): boolean {
  if (prop.type === "array") return !Array.isArray(value) || value.length === 0;
  if (prop.type === "boolean") return false;
  if (prop.type === "integer" || prop.type === "number") return value === "" || value == null;
  return value == null || value === "";
}

export function validateSchema(schema: JsonSchema | { properties: Record<string, JsonSchemaProperty>; required?: string[] }, values: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  Object.entries(schema.properties).forEach(([key, prop]) => {
    if (isSkippable(prop) || isConstField(prop)) return;
    const value = values?.[key];

    if (prop.oneOf?.length) {
      const discriminatorIndex = prop.oneOf.findIndex((variant) => {
        const discriminator = Object.entries(variant.properties).find(([, p]) => p.const !== undefined);
        return discriminator ? value?.[discriminator[0]] === discriminator[1].const : false;
      });
      const selectedIndex = discriminatorIndex !== -1 ? discriminatorIndex : simplestVariantIndex(prop.oneOf);
      const variant = prop.oneOf[selectedIndex];
      Object.entries(variant.properties).forEach(([k, p]) => {
        if (isSkippable(p) || isConstField(p)) return;
        if (variant.required?.includes(k) && isEmptyValue(p, value?.[k])) {
          errors[`${key}.${k}`] = `${p.title || humanize(k)} is required.`;
        }
      });
      return;
    }

    if (schema.required?.includes(key) && isEmptyValue(prop, value)) {
      errors[key] = `${prop.title || humanize(key)} is required.`;
    }
  });
  return errors;
}

// Several real connector specs (e.g. Facebook Marketing's Ad Account ID
// field) embed real `<a href="...">` links in their own `description` text —
// rendering that as plain text was showing the raw tags verbatim instead of
// a link. Rendered as HTML instead, with links opening in a new tab so
// clicking one doesn't abandon the setup form.
const FieldDescription: React.FC<{ html: string }> = ({ html }) => (
  <p
    className="text-xs text-gray-600 dark:text-gray-300 mt-1.5 leading-relaxed [&_a]:text-indigo-600 [&_a]:dark:text-indigo-400 [&_a]:underline [&_a]:hover:text-indigo-700"
    dangerouslySetInnerHTML={{ __html: html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ') }}
  />
);

const Field: React.FC<{ label: string; required?: boolean; description?: string; error?: string; children: React.ReactNode }> = ({
  label,
  required,
  description,
  error,
  children,
}) => (
  <div className="mb-3.5">
    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">
      {label}
      {required && <span className="text-rose-500"> *</span>}
    </label>
    {children}
    {error ? <p className="text-[11px] text-rose-500 mt-1">{error}</p> : description ? <FieldDescription html={description} /> : null}
  </div>
);

const PropertyField: React.FC<{
  propKey: string;
  prop: JsonSchemaProperty;
  required: boolean;
  value: any;
  onChange: (v: any) => void;
  errors?: Record<string, string>;
  errorKey?: string;
}> = ({ propKey, prop, required, value, onChange, errors, errorKey }) => {
  const label = prop.title || humanize(propKey);
  const error = errors?.[errorKey ?? propKey];
  const errorCls = error ? " border-rose-400 dark:border-rose-500 focus:ring-rose-200" : "";

  if (prop.oneOf?.length) {
    const discriminatorIndex = prop.oneOf.findIndex((variant) => {
      const discriminator = Object.entries(variant.properties).find(([, p]) => p.const !== undefined);
      return discriminator ? value?.[discriminator[0]] === discriminator[1].const : false;
    });
    const selectedIndex = discriminatorIndex !== -1 ? discriminatorIndex : simplestVariantIndex(prop.oneOf);
    const variant = prop.oneOf[selectedIndex];
    const variantEntries = sortedEntries(variant.properties);

    return (
      <div className="mb-3.5 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
        <Field label={label} required={required}>
          <select
            value={selectedIndex}
            onChange={(e) => {
              const next = prop.oneOf![Number(e.target.value)];
              onChange(defaultsForSchema({ properties: next.properties }));
            }}
            className={inputCls}
          >
            {prop.oneOf.map((v, i) => (
              <option key={i} value={i}>
                {v.title || `Option ${i + 1}`}
              </option>
            ))}
          </select>
        </Field>
        {variantEntries.map(([k, p]) => (
          <PropertyField
            key={k}
            propKey={k}
            prop={p}
            required={!!variant.required?.includes(k)}
            value={value?.[k]}
            onChange={(v) => onChange({ ...value, [k]: v })}
            errors={errors}
            errorKey={`${propKey}.${k}`}
          />
        ))}
      </div>
    );
  }

  if (prop.type === "array" && prop.items?.enum) {
    // A bordered checkbox list rather than pill toggles — matches how
    // Airbyte's own UI presents a multi-select enum field (e.g. Campaign/
    // AdSet/Ad Statuses, Action breakdowns), with a "Select all" row for the
    // common case of wanting every option.
    const options: string[] = prop.items.enum;
    const selected: string[] = Array.isArray(value) ? value : [];
    const allChecked = options.length > 0 && selected.length === options.length;
    return (
      <Field label={label} required={required} description={prop.description} error={error}>
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <label className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={() => onChange(allChecked ? [] : [...options])}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
            />
            Select all
          </label>
          <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
            {options.map((opt) => {
              const checked = selected.includes(opt);
              return (
                <label
                  key={opt}
                  className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(checked ? selected.filter((o) => o !== opt) : [...selected, opt])}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        </div>
      </Field>
    );
  }

  if (prop.type === "array") {
    const selected: string[] = Array.isArray(value) ? value : [];
    return (
      <Field label={label} required={required} description={prop.description} error={error}>
        <input
          value={selected.join(", ")}
          onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          placeholder={prop.examples?.[0] ? `e.g. ${prop.examples[0]}` : "Comma-separated"}
          className={inputCls + errorCls}
        />
      </Field>
    );
  }

  if (prop.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 mb-3.5 cursor-pointer">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
        />
        {label}
      </label>
    );
  }

  if (prop.format === "date-time") {
    return (
      <Field label={label} required={required} description={prop.description} error={error}>
        <input
          type="datetime-local"
          value={isoToDatetimeLocal(value)}
          onChange={(e) => onChange(datetimeLocalToIso(e.target.value))}
          className={inputCls + errorCls}
        />
      </Field>
    );
  }

  if (prop.type === "integer" || prop.type === "number") {
    return (
      <Field label={label} required={required} description={prop.description} error={error}>
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          placeholder={prop.default !== undefined ? String(prop.default) : undefined}
          className={inputCls + errorCls}
        />
      </Field>
    );
  }

  if (prop.enum) {
    return (
      <Field label={label} required={required} description={prop.description} error={error}>
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls + errorCls}>
          {!required && <option value="">—</option>}
          {prop.enum.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  return (
    <Field label={label} required={required} description={prop.description} error={error}>
      <input
        type={prop.airbyte_secret ? "password" : "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={prop.examples?.[0] ? `e.g. ${prop.examples[0]}` : undefined}
        className={inputCls + errorCls}
      />
    </Field>
  );
};

export const DynamicSchemaForm: React.FC<{
  schema: JsonSchema;
  values: Record<string, any>;
  onChange: (patch: Record<string, any>) => void;
  errors?: Record<string, string>;
}> = ({ schema, values, onChange, errors }) => {
  const entries = sortedEntries(schema.properties);
  const skippedAdvanced = Object.entries(schema.properties).filter(([, p]) => p.type === "array" && p.items?.type === "object");

  return (
    <div>
      {entries.map(([key, prop]) => (
        <PropertyField
          key={key}
          propKey={key}
          prop={prop}
          required={!!schema.required?.includes(key)}
          value={values[key]}
          onChange={(v) => onChange({ [key]: v })}
          errors={errors}
        />
      ))}
      {skippedAdvanced.length > 0 && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
          Advanced option{skippedAdvanced.length > 1 ? "s" : ""} ({skippedAdvanced.map(([k]) => humanize(k)).join(", ")}) left at the connector's default —
          not editable here yet.
        </p>
      )}
    </div>
  );
};
